/**
 * Matching a field the Studio is pointing at to an element on the page.
 *
 * Sanity already scrolls to and highlights an element when the Studio focuses a field — but only when
 * the element's path equals the Studio's path *exactly* (`e.sanity.path === message.data.path` in the
 * overlay's reducer). A field with no element of its own matches nothing, and nothing happens. These
 * helpers find the most specific element that *contains* the field instead, so the editor still sees
 * where they are: the block that owns it.
 *
 * Only marks this lane wrote are considered — JSON `data-sanity` values (see `edit-attribute.ts`).
 * Text found through stega has no attribute and stays Sanity's to handle.
 */

/** Sanity's own channel. The Studio posts `presentation/focus` and `presentation/blur` on it. */
export const COMLINK_DOMAIN = 'sanity/comlink';

/**
 * The channel the Studio-side hover helper posts on. Ours, not Sanity's: the protocol between the Studio
 * and the overlay defines focus and blur but no hover, so there is nothing to hook.
 */
export const SYNC_DOMAIN = 'eneco/studio-sync';

export interface Mark {
  element: Element;
  /** The published id; the Studio and the overlay do not agree on whether a `drafts.` prefix is present. */
  id: string;
  /** Studio path, as written by `createEditAttribute`. */
  path: string;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export const publishedId = (id: string): string => id.replace(/^drafts\./, '');

/** Every element the lane marked, with where it points. Anything that is not our JSON is skipped. */
export const readMarks = (root: ParentNode = document): Mark[] => {
  const marks: Mark[] = [];
  for (const element of Array.from(root.querySelectorAll('[data-sanity]'))) {
    const raw = element.getAttribute('data-sanity');
    if (!raw?.startsWith('{')) continue;
    try {
      const node: unknown = JSON.parse(raw);
      if (isObject(node) && typeof node.id === 'string' && typeof node.path === 'string') {
        marks.push({ element, id: publishedId(node.id), path: node.path });
      }
    } catch {
      // Not ours: leave it to Sanity.
    }
  }
  return marks;
};

/**
 * Is `path` the same field as `ancestor`, or somewhere inside it?
 *
 * The next character has to be a separator: `items` contains `items.title` and `items[_key=="a"]`,
 * but not `itemsOther`.
 */
export const isWithin = (path: string, ancestor: string): boolean =>
  path === ancestor || path.startsWith(`${ancestor}.`) || path.startsWith(`${ancestor}[`);

export interface Resolved {
  mark: Mark;
  /** The mark's path is the path itself, not just a container of it. */
  isExact: boolean;
}

/** The most specific mark in `id` that is, or contains, `path`. Deepest wins: it is the longest path. */
export const resolveMark = (marks: readonly Mark[], id: string, path: string): Resolved | undefined => {
  const wanted = publishedId(id);
  let best: Mark | undefined;
  for (const mark of marks) {
    if (mark.id !== wanted || !isWithin(path, mark.path)) continue;
    if (!best || mark.path.length > best.path.length) best = mark;
  }
  return best ? { mark: best, isExact: best.path === path } : undefined;
};

/** Does any part of the element lie inside the viewport? */
export const isInViewport = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
};
